<script setup lang="ts">
import type { BookingSlotsDay } from '../../model/slots-management/interfaces'

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'

import { AppMultiSelect } from '@shared/ui/selects'
import { AppTooltip } from '@shared/ui/tooltips'
import { AppTable, AppTh, AppTr, AppTd } from '@shared/ui/tables'
import { AppInput } from '@shared/ui/inputs'
import { AppIcon } from '@shared/ui/icons'
import { BtnToggle } from '@shared/ui/buttons'

import { PREVIOUS_SHIFT_SLOTS_COUNT, TIMEZONE_GROUP_OPTIONS } from '../../config/slots-management'

defineOptions({
  name: 'BookingSlotsTable',
})

const props = withDefaults(defineProps<{
  day: BookingSlotsDay
  show?: boolean
}>(), {
  show: false,
})

const timezoneOptions = TIMEZONE_GROUP_OPTIONS

const toggleSlot = (value: boolean, index: number): void => {
  const slot = props.day.slots[index]
  if (slot) {
    slot.enable_slot = value
  }
}

const isPreviousShift = (index: number): boolean => index < PREVIOUS_SHIFT_SLOTS_COUNT
</script>

<template>
  <div
    v-show="show"
    class="booking-slots-table"
  >
    <AppTable
      class="rounded-table"
      :paginated="false"
    >
      <template #head>
        <AppTr>
          <AppTh>Time slot (UTC)</AppTh>
          <AppTh>Local Time</AppTh>
          <AppTh>Enable slot</AppTh>
          <AppTh>
            <span class="booking-slots-table__th-tooltip">
              Pre-shift limits
              <AppTooltip
                text="This column shows the total booking limits for days prior to the shift date."
                size="small"
              >
                <AppIcon
                  :icon="faCircleInfo"
                  size="small"
                  color="slate-400"
                />
              </AppTooltip>
            </span>
          </AppTh>
          <AppTh>
            <span class="booking-slots-table__th-tooltip">
              Total limits
              <AppTooltip
                text="This column shows the total booking limits for the shift. The limits become active at 06:00 UTC on the shift calendar date."
                size="small"
              >
                <AppIcon
                  :icon="faCircleInfo"
                  size="small"
                  color="slate-400"
                />
              </AppTooltip>
            </span>
          </AppTh>
          <AppTh>
            <span class="booking-slots-table__th-tooltip">
              Sales Max
              <AppTooltip
                text="This column shows booking limits based on Sales team capacity."
                size="small"
              >
                <AppIcon
                  :icon="faCircleInfo"
                  size="small"
                  color="slate-400"
                />
              </AppTooltip>
            </span>
          </AppTh>
          <AppTh>Bookings</AppTh>
          <AppTh>Disable time zone</AppTh>
        </AppTr>
      </template>
      <template #body>
        <AppTr
          v-for="(slot, index) in day.slots"
          :key="slot.time"
        >
          <AppTd>
            <div class="booking-slots-table__time">
              <span>{{ slot.time }}</span>
              <AppTooltip
                v-if="isPreviousShift(index)"
                text="This slot belongs to the previous day's shift"
                size="small"
              >
                <span class="booking-slots-table__prev-shift">prev. shift</span>
              </AppTooltip>
            </div>
          </AppTd>
          <AppTd>{{ slot.time_local }}</AppTd>
          <AppTd>
            <BtnToggle
              :checked="slot.enable_slot"
              @click="(value) => toggleSlot(value, index)"
            />
          </AppTd>
          <AppTd>
            <AppInput
              v-model="slot.pre_shift_limit"
              type="number"
              :min="0"
              :disabled="!slot.enable_slot"
            />
          </AppTd>
          <AppTd>
            <AppInput
              v-model="slot.limit"
              type="number"
              :min="0"
              :disabled="!slot.enable_slot"
            />
          </AppTd>
          <AppTd>
            <AppInput
              v-model="slot.sales_max"
              type="number"
              :min="0"
              :disabled="!slot.enable_slot"
            />
          </AppTd>
          <AppTd>
            <a
              v-if="slot.bookings_link"
              :href="slot.bookings_link"
              target="_blank"
              rel="noopener noreferrer"
            >{{ slot.bookings }}</a>
            <span v-else>{{ slot.bookings }}</span>
          </AppTd>
          <AppTd>
            <AppMultiSelect
              v-model="slot.disabled_timezones"
              :options="timezoneOptions"
              :disabled="!slot.enable_slot"
              placeholder="None"
            />
          </AppTd>
        </AppTr>
      </template>
    </AppTable>
  </div>
</template>

<style scoped lang="scss" src="./BookingSlotsTable.scss" />
